/**
 * Site Attendance Detail Page - View session details with guard list (Roll-Call Model)
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Button,
  Badge,
  tokens,
  Spinner,
  Avatar,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  PersonRegular,
  BuildingRegular,
  ClockRegular,
  LocationRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  PersonAvailableRegular,
  ShieldCheckmarkRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate } from '../../../components/common';
import { ApiClient, newToast } from '../../../configurations/index.config';
import type { AttendanceSessionDetail, AttendanceGuardRecord } from '../../../types';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  backButton: {
    minWidth: 'auto',
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginBottom: '24px',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  card: {
    padding: '24px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  cardIcon: {
    fontSize: '20px',
    color: tokens.colorBrandForeground1,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  infoLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#605e5c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#323130',
  },
  statusBadge: {
    marginTop: '4px',
  },
  timelineSection: {
    marginTop: '8px',
  },
  timelineItem: {
    display: 'flex',
    gap: '16px',
    padding: '16px 0',
    borderBottom: '1px solid #e1dfdd',
    ':last-child': {
      borderBottom: 'none',
    },
  },
  timelineIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    flexShrink: 0,
  },
  timelineContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  timelineTitle: {
    fontWeight: '600',
    color: '#323130',
  },
  timelineTime: {
    fontSize: '14px',
    color: '#605e5c',
  },
  timelineLocation: {
    fontSize: '13px',
    color: '#605e5c',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  verificationSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    marginTop: '16px',
  },
  verificationIcon: {
    fontSize: '32px',
  },
  verificationContent: {
    flex: 1,
  },
  verificationTitle: {
    fontWeight: '600',
    color: '#323130',
  },
  verificationSubtitle: {
    fontSize: '13px',
    color: '#605e5c',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  notFoundContainer: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  thCenter: {
    padding: '12px 16px',
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  tdCenter: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
    textAlign: 'center',
  },
  guardInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  guardDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  guardName: {
    fontWeight: '500',
  },
  guardId: {
    fontSize: '12px',
    color: '#605e5c',
  },
  confidenceBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  progressBarContainer: {
    width: '60px',
    height: '6px',
    backgroundColor: '#e1dfdd',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: '3px',
  },
  summaryStats: {
    display: 'flex',
    gap: '24px',
    marginTop: '16px',
  },
  summaryStat: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
});

export const SiteAttendanceDetailFluentUI: React.FC = () => {
  const styles = useStyles();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [sessionData, setSessionData] = useState<AttendanceSessionDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchSessionDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSessionDetail = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get(`/get-attendance-session/${id}`);
      if (response.data.status === 'success') {
        const data = response.data.data;
        // Deduplicate records by record_id to handle backend join duplicates
        if (data?.records) {
          data.records = data.records.filter(
            (record: AttendanceGuardRecord, index: number, self: AttendanceGuardRecord[]) =>
              index === self.findIndex(r => r.record_id === record.record_id)
          );
        }
        setSessionData(data);
      } else {
        newToast('error', 'Failed to load session details');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const getGuardStatusBadge = (status: string) => {
    switch (status) {
      case 'present':
        return (
          <Badge appearance="filled" color="success">
            Present
          </Badge>
        );
      case 'absent':
        return (
          <Badge appearance="filled" color="danger">
            Absent
          </Badge>
        );
      default:
        return (
          <Badge appearance="filled" color="subtle">
            {status}
          </Badge>
        );
    }
  };

  const getVerificationBadge = (status: string | null) => {
    switch (status) {
      case 'verified':
        return (
          <Badge appearance="filled" color="success">
            Verified
          </Badge>
        );
      case 'pending':
        return (
          <Badge appearance="filled" color="warning">
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge appearance="filled" color="danger">
            Failed
          </Badge>
        );
      default:
        return (
          <Badge appearance="filled" color="subtle">
            N/A
          </Badge>
        );
    }
  };

  const getSessionStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge appearance="filled" color="success">
            Completed
          </Badge>
        );
      case 'expired':
        return (
          <Badge appearance="filled" color="warning">
            Expired
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge appearance="filled" color="danger">
            Cancelled
          </Badge>
        );
      default:
        return (
          <Badge appearance="filled" color="subtle">
            {status}
          </Badge>
        );
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) {
      return '—';
    }
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatTime = (timeString: string | null) => {
    if (!timeString) {
      return '—';
    }
    if (timeString.includes('T')) {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
    return timeString;
  };

  const formatGpsCoords = (lat: number | string | null, lng: number | string | null) => {
    if (!lat || !lng) {
      return 'Not available';
    }
    return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
  };

  const getConfidenceColor = (confidence: number | string | null) => {
    const value = Number(confidence) || 0;
    if (!value) {
      return '#e1dfdd';
    }
    if (value >= 80) {
      return '#107c10';
    }
    if (value >= 50) {
      return '#ffaa44';
    }
    return '#d13438';
  };

  const getInitials = (record: AttendanceGuardRecord) => {
    return `${record.first_name?.charAt(0) || ''}${record.last_name?.charAt(0) || ''}`;
  };

  if (loading) {
    return (
      <PermissionGate privilege="get-site-attendance-history">
        <div className={styles.container}>
          <div className={styles.loadingContainer}>
            <Spinner size="large" label="Loading session details..." />
          </div>
        </div>
      </PermissionGate>
    );
  }

  if (!sessionData) {
    return (
      <PermissionGate privilege="get-site-attendance-history">
        <div className={styles.container}>
          <div className={styles.pageHeader}>
            <Button
              className={styles.backButton}
              appearance="subtle"
              icon={<ArrowLeftRegular />}
              onClick={() => navigate('/account/security-operations/site-attendance')}
            >
              Back
            </Button>
          </div>
          <ContentCard>
            <div className={styles.notFoundContainer}>
              <Text size={500} weight="semibold" style={{ display: 'block' }}>
                Attendance session not found
              </Text>
              <Text style={{ marginTop: '8px', display: 'block' }}>
                The attendance session you're looking for doesn't exist or has been removed.
              </Text>
            </div>
          </ContentCard>
        </div>
      </PermissionGate>
    );
  }

  const { session, records } = sessionData;
  const totalGuards = Number(session?.total_guards) || 0;
  const presentCount = Number(session?.present_count) || 0;
  const attendancePercentage =
    totalGuards > 0 ? ((presentCount / totalGuards) * 100).toFixed(1) : '0';

  return (
    <PermissionGate privilege="get-site-attendance-history">
      <div className={styles.container}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <Button
            className={styles.backButton}
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => navigate('/account/security-operations/site-attendance')}
          >
            Back
          </Button>
          <div className={styles.headerText}>
            <Text className={styles.pageTitle}>Session Details</Text>
            <Text className={styles.pageSubtitle}>
              {session.site_name} • {formatDateTime(session.session_start)}
            </Text>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className={styles.grid}>
          {/* Session Information */}
          <ContentCard>
            <div className={styles.card}>
              <Text className={styles.cardTitle}>
                <ClockRegular className={styles.cardIcon} />
                Session Information
              </Text>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <Text className={styles.infoLabel}>Conducted By</Text>
                  <Text className={styles.infoValue}>{session.conducted_by}</Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.infoLabel}>Status</Text>
                  <div className={styles.statusBadge}>{getSessionStatusBadge(session.status)}</div>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.infoLabel}>Start Time</Text>
                  <Text className={styles.infoValue}>{formatTime(session.session_start)}</Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.infoLabel}>End Time</Text>
                  <Text className={styles.infoValue}>{formatTime(session.session_end)}</Text>
                </div>
                {session.checkpoint_name && (
                  <div className={styles.infoItem}>
                    <Text className={styles.infoLabel}>Checkpoint</Text>
                    <Text className={styles.infoValue}>{session.checkpoint_name}</Text>
                  </div>
                )}
              </div>
            </div>
          </ContentCard>

          {/* Site Information */}
          <ContentCard>
            <div className={styles.card}>
              <Text className={styles.cardTitle}>
                <BuildingRegular className={styles.cardIcon} />
                Site Information
              </Text>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <Text className={styles.infoLabel}>Site</Text>
                  <Text className={styles.infoValue}>{session.site_name}</Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.infoLabel}>Client</Text>
                  <Text className={styles.infoValue}>{session.client_name}</Text>
                </div>
                <div className={styles.infoItem}>
                  <Text className={styles.infoLabel}>GPS Location</Text>
                  <Text className={styles.infoValue}>
                    {formatGpsCoords(session.gps_lat, session.gps_lng)}
                  </Text>
                </div>
              </div>
            </div>
          </ContentCard>

          {/* Attendance Summary */}
          <ContentCard>
            <div className={styles.card}>
              <Text className={styles.cardTitle}>
                <PersonAvailableRegular className={styles.cardIcon} />
                Attendance Summary
              </Text>
              <div className={styles.verificationSection} style={{ backgroundColor: '#f5f5f5' }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <Text style={{ fontSize: '36px', fontWeight: '600', color: '#323130' }}>
                    {attendancePercentage}%
                  </Text>
                  <Text style={{ display: 'block', fontSize: '13px', color: '#605e5c' }}>
                    Attendance Rate
                  </Text>
                </div>
              </div>
              <div className={styles.summaryStats}>
                <div className={styles.summaryStat}>
                  <CheckmarkCircleRegular style={{ color: '#107c10', fontSize: '20px' }} />
                  <Text>
                    <strong>{session.present_count}</strong> Present
                  </Text>
                </div>
                <div className={styles.summaryStat}>
                  <DismissCircleRegular style={{ color: '#d13438', fontSize: '20px' }} />
                  <Text>
                    <strong>{session.absent_count}</strong> Absent
                  </Text>
                </div>
                <div className={styles.summaryStat}>
                  <PersonRegular style={{ color: '#605e5c', fontSize: '20px' }} />
                  <Text>
                    <strong>{session.total_guards}</strong> Total
                  </Text>
                </div>
              </div>
            </div>
          </ContentCard>

          {/* Location Info */}
          <ContentCard>
            <div className={styles.card}>
              <Text className={styles.cardTitle}>
                <LocationRegular className={styles.cardIcon} />
                Location Details
              </Text>
              <div className={styles.verificationSection} style={{ backgroundColor: '#e8f4fd' }}>
                <LocationRegular style={{ fontSize: '32px', color: '#0078d4' }} />
                <div className={styles.verificationContent}>
                  <Text className={styles.verificationTitle}>Session Location</Text>
                  <Text className={styles.verificationSubtitle}>
                    {formatGpsCoords(session.gps_lat, session.gps_lng)}
                  </Text>
                </div>
              </div>
            </div>
          </ContentCard>

          {/* Guard Records Table */}
          <div className={styles.fullWidth}>
            <ContentCard noPadding>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #e1dfdd' }}>
                <Text className={styles.cardTitle} style={{ marginBottom: 0 }}>
                  <ShieldCheckmarkRegular className={styles.cardIcon} />
                  Guard Attendance Records ({records.length})
                </Text>
              </div>
              {records.length === 0 ? (
                <div className={styles.emptyState}>
                  <Text>No guard records found for this session</Text>
                </div>
              ) : (
                <table className={styles.table}>
                  <thead className={styles.tableHeader}>
                    <tr>
                      <th className={styles.th}>Guard</th>
                      <th className={styles.th}>Phone</th>
                      <th className={styles.thCenter}>Status</th>
                      <th className={styles.thCenter}>Verification</th>
                      <th className={styles.thCenter}>Confidence</th>
                      <th className={styles.thCenter}>Scanned At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record: AttendanceGuardRecord) => (
                      <tr key={record.record_id} className={styles.tr}>
                        <td className={styles.td}>
                          <div className={styles.guardInfo}>
                            <Avatar
                              name={`${record.first_name} ${record.last_name}`}
                              initials={getInitials(record)}
                              image={record.face_url ? { src: record.face_url } : undefined}
                              size={36}
                            />
                            <div className={styles.guardDetails}>
                              <Text className={styles.guardName}>
                                {record.first_name} {record.last_name}
                              </Text>
                              <Text className={styles.guardId}>{record.unique_emp_id}</Text>
                            </div>
                          </div>
                        </td>
                        <td className={styles.td}>
                          <Text>{record.phone || '—'}</Text>
                        </td>
                        <td className={styles.tdCenter}>{getGuardStatusBadge(record.status)}</td>
                        <td className={styles.tdCenter}>
                          {getVerificationBadge(record.verification_status)}
                        </td>
                        <td className={styles.tdCenter}>
                          {record.face_confidence !== null ? (
                            <div className={styles.confidenceBar}>
                              <div className={styles.progressBarContainer}>
                                <div
                                  className={styles.progressBar}
                                  style={{
                                    width: `${Number(record.face_confidence) || 0}%`,
                                    backgroundColor: getConfidenceColor(record.face_confidence),
                                  }}
                                />
                              </div>
                              <Text style={{ fontSize: '12px' }}>
                                {(Number(record.face_confidence) || 0).toFixed(0)}%
                              </Text>
                            </div>
                          ) : (
                            <Text>—</Text>
                          )}
                        </td>
                        <td className={styles.tdCenter}>
                          <Text>{formatTime(record.scanned_at)}</Text>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </ContentCard>
          </div>
        </div>
      </div>
    </PermissionGate>
  );
};

export default SiteAttendanceDetailFluentUI;
